import { useEffect, useReducer } from 'react';

import { useTimeout } from 'common/hooks/useTimeout';
import { useAppDispatch } from 'features/app/hooks';
import {
  VoiceMeetingEventBus,
  VoiceMeetingService,
} from 'features/voice-meeting/services';
import { VoiceSessionStatus } from 'features/voice-session/enums';
import { resetVoiceSession } from 'features/voice-session/store';

export const RECONNECT_CLEANUP_FAILOPEN_MS = 10000;

type UseReconnectCleanupPendingParams = {
  isInReconnectState: boolean;
  shouldWaitForCleanup?: boolean;
  voiceSessionStatus: VoiceSessionStatus;
  timeoutMs?: number;
};

type ReconnectCleanupState = {
  cycle: number;
  isPending: boolean;
  timedOutCycle: number | null;
};

type ReconnectCleanupAction =
  | { type: 'syncPending'; shouldBlockReconnect: boolean }
  | { type: 'timeoutElapsed' };

const initialReconnectCleanupState: ReconnectCleanupState = {
  cycle: 0,
  isPending: false,
  timedOutCycle: null,
};

const reconnectCleanupReducer = (
  state: ReconnectCleanupState,
  action: ReconnectCleanupAction
): ReconnectCleanupState => {
  switch (action.type) {
    case 'timeoutElapsed': {
      if (!state.isPending) {
        return state;
      }

      return {
        ...state,
        timedOutCycle: state.cycle,
      };
    }
    case 'syncPending': {
      if (!action.shouldBlockReconnect) {
        if (!state.isPending) {
          return state;
        }

        return {
          ...state,
          isPending: false,
        };
      }

      if (state.isPending) {
        return state;
      }

      return {
        cycle: state.cycle + 1,
        isPending: true,
        timedOutCycle: null,
      };
    }
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
};

export const useReconnectCleanupPending = ({
  isInReconnectState,
  shouldWaitForCleanup = true,
  voiceSessionStatus,
  timeoutMs = RECONNECT_CLEANUP_FAILOPEN_MS,
}: UseReconnectCleanupPendingParams) => {
  const appDispatch = useAppDispatch();
  // Only block while teardown is in progress. An ACTIVE session must remain
  // reconnectable so the existing voice session can be reused.
  const shouldBlockReconnect =
    isInReconnectState &&
    shouldWaitForCleanup &&
    voiceSessionStatus === VoiceSessionStatus.DISCONNECTING;

  const [state, dispatch] = useReducer(
    reconnectCleanupReducer,
    initialReconnectCleanupState
  );

  useEffect(() => {
    dispatch({ type: 'syncPending', shouldBlockReconnect });
  }, [shouldBlockReconnect]);

  useTimeout(
    () => {
      dispatch({ type: 'timeoutElapsed' });
      // Abandon in-flight/queued listener cleanup so it cannot tear down a
      // session created after fail-open reconnect.
      VoiceMeetingEventBus.invalidatePendingVoiceSessionCleanup();
      // Clear local session immediately so reconnect uses createNewVoiceSession
      // even if backend/Chime teardown is still hung in the cleanup queue.
      VoiceMeetingService.getInstance().resetService();
      appDispatch(resetVoiceSession());
    },
    state.isPending && state.timedOutCycle !== state.cycle ? timeoutMs : null
  );

  return state.isPending && state.timedOutCycle !== state.cycle;
};
